import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, waLink, formatApiError } from "../lib/api";
import { useSite } from "../lib/SiteContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const inputCls = "w-full rounded-lg border border-input bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-pine focus:ring-2 focus:ring-pine/15";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500";

const EnquiryForm = ({ defaultTour = "", type = "tour", onDone }) => {
  const { tours, content } = useSite();
  const [form, setForm] = useState({
    type, tour_name: defaultTour, date: "", adults: 2, children: 0,
    start_time: "", meeting_point: "", duration: "", name: "", email: "", phone: "", special_requests: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { setForm((f) => ({ ...f, type, tour_name: defaultTour })); }, [defaultTour, type]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (type === "tour" && !form.tour_name) e.tour_name = "Please choose a tour.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, adults: Number(form.adults), children: Number(form.children) });
      setSuccess(true);
      toast.success("Enquiry sent — we will reply personally soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center py-6 text-center" data-testid="enquiry-success">
        <CheckCircle2 className="h-14 w-14 text-pine" />
        <h3 className="mt-4 font-display text-2xl font-semibold text-pine">Thank you, {form.name.split(" ")[0]}!</h3>
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
          Your enquiry has been received. We will reply to <span className="font-medium text-pine">{form.email}</span> personally
          to confirm availability and details. Nothing is booked until we confirm with you.
        </p>
        <a
          href={waLink(content?.settings, `Hello Aloha Corfu Tours! I just sent an enquiry${form.tour_name ? ` about ${form.tour_name}` : ""} and would like to chat.`)}
          target="_blank" rel="noopener noreferrer"
          data-testid="enquiry-success-whatsapp"
          className="mt-6 inline-flex rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:scale-[1.03]"
        >
          Chat with us on WhatsApp
        </a>
        {onDone && (
          <button onClick={onDone} data-testid="enquiry-success-close" className="mt-3 text-sm font-medium text-gray-500 underline-offset-4 hover:underline">
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="enquiry-form">
      {type === "tour" && (
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="enquiry-tour">Tour *</label>
          <Select value={form.tour_name} onValueChange={(v) => set("tour_name", v)}>
            <SelectTrigger className="w-full rounded-lg" data-testid="enquiry-tour-select" id="enquiry-tour">
              <SelectValue placeholder="Choose a tour" />
            </SelectTrigger>
            <SelectContent>
              {tours.map((t) => (
                <SelectItem key={t.id} value={t.name} data-testid={`enquiry-tour-option-${t.slug}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.tour_name && <p className="mt-1 text-xs text-red-600" data-testid="enquiry-error-tour">{errors.tour_name}</p>}
        </div>
      )}

      <div>
        <label className={labelCls} htmlFor="enquiry-date">Preferred date</label>
        <input id="enquiry-date" type="date" min={new Date().toISOString().split("T")[0]} className={inputCls}
               value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="enquiry-date-input" />
      </div>
      <div>
        <label className={labelCls} htmlFor="enquiry-time">Preferred start time</label>
        <input id="enquiry-time" type="time" className={inputCls} value={form.start_time}
               onChange={(e) => set("start_time", e.target.value)} data-testid="enquiry-time-input" />
      </div>
      <div>
        <label className={labelCls} htmlFor="enquiry-adults">Adults</label>
        <input id="enquiry-adults" type="number" min="1" max="30" className={inputCls} value={form.adults}
               onChange={(e) => set("adults", e.target.value)} data-testid="enquiry-adults-input" />
      </div>
      <div>
        <label className={labelCls} htmlFor="enquiry-children">Children</label>
        <input id="enquiry-children" type="number" min="0" max="20" className={inputCls} value={form.children}
               onChange={(e) => set("children", e.target.value)} data-testid="enquiry-children-input" />
      </div>
      <div>
        <label className={labelCls} htmlFor="enquiry-duration">Preferred duration</label>
        <Select value={form.duration} onValueChange={(v) => set("duration", v)}>
          <SelectTrigger className="w-full rounded-lg" data-testid="enquiry-duration-select" id="enquiry-duration">
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Half day (up to 4 hours)">Half day (up to 4 hours)</SelectItem>
            <SelectItem value="Full day (5–8 hours)">Full day (5–8 hours)</SelectItem>
            <SelectItem value="Flexible / not sure yet">Flexible / not sure yet</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className={labelCls} htmlFor="enquiry-meeting">Hotel / meeting point</label>
        <input id="enquiry-meeting" type="text" placeholder="e.g. Hotel name, villa, port" className={inputCls}
               value={form.meeting_point} onChange={(e) => set("meeting_point", e.target.value)} data-testid="enquiry-meeting-input" />
      </div>
      <div>
        <label className={labelCls} htmlFor="enquiry-name">Your name *</label>
        <input id="enquiry-name" type="text" placeholder="Full name" className={inputCls}
               value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="enquiry-name-input" />
        {errors.name && <p className="mt-1 text-xs text-red-600" data-testid="enquiry-error-name">{errors.name}</p>}
      </div>
      <div>
        <label className={labelCls} htmlFor="enquiry-email">Email address *</label>
        <input id="enquiry-email" type="email" placeholder="you@example.com" className={inputCls}
               value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="enquiry-email-input" />
        {errors.email && <p className="mt-1 text-xs text-red-600" data-testid="enquiry-error-email">{errors.email}</p>}
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor="enquiry-phone">Telephone / WhatsApp number</label>
        <input id="enquiry-phone" type="tel" placeholder="+44 ..." className={inputCls}
               value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="enquiry-phone-input" />
      </div>
      <div className="sm:col-span-2">
        <label className={labelCls} htmlFor="enquiry-requests">
          {type === "custom" ? "Tell us what you would love to see and do" : "Special requests"}
        </label>
        <textarea id="enquiry-requests" rows={3} className={inputCls}
                  placeholder={type === "custom" ? "Beaches, villages, food, history, photography... anything you love" : "Anything we should know? Dietary needs, mobility, occasions..."}
                  value={form.special_requests} onChange={(e) => set("special_requests", e.target.value)}
                  data-testid="enquiry-requests-input" />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="enquiry-submit-button"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-pine-light disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? "Sending..." : "Send Enquiry"}
        </button>
        <p className="mt-3 text-center text-xs text-gray-400">
          No payment is taken online. We confirm every enquiry personally — prices on request.
        </p>
      </div>
    </form>
  );
};

export default EnquiryForm;
